/* eslint-disable max-len */
import styled from 'styled-components'
import Colors from '../../theme/colors'
import Fonts from '../../theme/fonts'
import ExternalLink from '../Icons/deprecatedIcons/external-link/ExternalLink'

const buttonStyles = props => `
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: none;
  padding: 12px 16px 12px 16px;
  font-family: ${Fonts.fontFamilies.Inter};
  font-size: ${Fonts.fontSizes.p16};
  line-height: 16px;
  min-width: 128px;

  height: ${props.size === 'large' ? '48px' : '40px'};

  svg {
    margin-right: 0.5rem;
    height: ${props.svgHeight ? `${props.svgHeight}px` : '14px'};
  }

  :disabled {
    background-color: ${Colors.Primary.GreyLight};
    color: ${Colors.Primary.Grey};
    cursor: not-allowed;

    svg {
      path {
        fill: ${Colors.Primary.Grey};
      }
    }
  }

  :not(:disabled):hover {
    cursor: pointer;
  }
`

const linkStyles = props => (
  (props.linkStyle === 'button') ? `
    position: relative;
    display: inline-block;
    border: 1px solid ${Colors.Primary.Black} !important;
    border-radius: 5px;
    font-family: ${Fonts.fontFamilies.Montserrat};
    height: auto;
    padding: .875rem 2rem;
    line-height: 1.5rem;

    :hover {
      text-decoration: none;
      -webkit-text-decoration: none;
    }
  
    svg {
      position: absolute;
      top: 0.125rem;
      right: 2.75rem;
      bottom: 0;
      margin: auto 0;
    }
    ` :
  null
)

export const PrimaryYellow = styled.button`
  ${props => buttonStyles(props)}

  color: ${Colors.Primary.GreyText};
  background-color: ${Colors.Primary.yellow500};

  :not(:disabled):hover {
    background-color: ${Colors.Primary.yellow600};
  }
`

export const PrimaryBlack = styled.button`
  ${props => buttonStyles(props)}

  color: ${Colors.Primary.White};
  background-color: ${Colors.Primary.Black};

  :not(:disabled):hover {
    background-color: ${Colors.Primary.GreyDark};
  }

  svg {
    path {
      fill: ${Colors.Primary.White};
    }
  }
`

export const SecondaryButton = styled.button`
  ${props => buttonStyles(props)}

  background-color: ${Colors.Primary.White};
  border: 1px solid ${Colors.Primary.GreyTextLight};

  :hover {
    background-color: ${Colors.Primary.GreyWhite};
    color: ${Colors.Primary.GreyText};
  }

  :disabled {
    border-color: ${Colors.Primary.Grey};
    background-color: ${Colors.Primary.White};
    color: ${Colors.Primary.Grey};
  }
`

const LinkButtonBase = styled.a<{ linkStyle?: string }>`
  ${props => buttonStyles(props)}

  background-color: rgba(0,0,0,0);
  border: none;
  padding: 12px 16px 12px 16px;
  font-family: ${Fonts.fontFamilies.Inter};
  font-size: ${Fonts.fontSizes.p16};
  line-height: 16px;
  text-underline-position: under;

  :hover {
    text-decoration-line: underline;
    -webkit-text-decoration-line: underline;
    text-decoration-thickness: 1px;
    -webkit-text-decoration-thickness: 1px;
    text-decoration-style: solid;
    -webkit-text-decoration-style: solid;
    text-decoration-color: ${Colors.Primary.GreyText};
    -webkit-text-decoration-color: ${Colors.Primary.GreyText};
  }

  :disabled {
    color: ${Colors.Primary.Grey};
    text-decoration-color: ${Colors.Primary.Grey};
    -webkit-text-decoration-color: ${Colors.Primary.Grey};
  }
  
  ${props => linkStyles(props)}
`

export const LinkButton = ({
  children,
  isExternal = false,
  dataGtm = null,
  dataSlug = null,
  ...props
}) => (
  <LinkButtonBase
    {...{
      "data-gtm": dataGtm,
      "data-slug": dataSlug
    }}
    {...(isExternal
      ? {
          target: "_blank",
          rel: "noopener noreferrer external"
        }
      : {})}
    {...props}
  >
    {children}
    {isExternal && <ExternalLink kind="small" />}
  </LinkButtonBase>
)

export const ActionButtonYellow = styled.button`
  ${props => buttonStyles(props)}

  color: ${Colors.Primary.GreyText};
  background-color: ${Colors.Primary.yellow500};

  :not(:disabled):hover {
    background-color: ${Colors.Primary.yellow600};
  }

  display: inline-block;
  height: auto;
  min-width: 10rem;
  border-radius: 5px;
  padding: 16px 24px;
  font-family: inherit;
  font-weight: 500;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const SquareButtonBase = styled.button`
  ${props => buttonStyles(props)}

  background-color: transparent;
  border: 1px solid ${Colors.Primary.Black};
  border-radius: .625rem;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  cursor: pointer;
  box-shadow: none;
  transition-property: box-shadow, opacity;
  transition-duration: .2s;
  transition-timing-function: ease-in-out;
  min-width: auto;

  & svg {
    margin: 0;
    height: auto;
  }

  :focus {
    outline: 0;
    box-shadow: 0 0 0 1px ${Colors.Primary.yellow600};
  }

  :hover {
    opacity: 0.75;
  }
`

export const SquareButton = ({
  children,
  onClick,
  forwardRef = null,
  className,
  ...props
}) => (
  <SquareButtonBase
    {...{
      className,
      onClick
    }} // eslint-disable-next-line
    data-testid={props["data-testid"]}
    type={props.htmlType || 'button'}
    ref={forwardRef}
  >
    {children}
  </SquareButtonBase>
)
